import { api } from './client'
import type { Carte } from './feed'

/**
 * Le Parcours — 02 §F3, 03 §2.
 *
 * Quatre niveaux : `COURS → MODULE → CHAPITRE → SECTION`. Le plan d'un cours
 * arrive en un seul appel, avec l'état de chaque porte — `ouverte`, `terminee`
 * — déjà calculé par le serveur. **L'écran ne recalcule aucun verrou** : il
 * n'en a pas les moyens, et deux calculs du même verrou finiraient par
 * diverger (CA-3.1).
 */

export interface SectionDuPlan {
  id: number
  slug: string
  titre: string
  /** Une promesse faite AVANT de commencer : ai-je le temps maintenant ? */
  minutes: number
  terminee: boolean
  ouverte: boolean
  /**
   * Combien d'exercices SERVABLES porte cette section — [DECISIONS §169].
   *
   * **Zéro veut dire « pas de bouton »**, et pas un bouton grisé : il n'y a
   * rien à débloquer, il n'y a rien derrière. Sur 377 sections publiées, 40
   * en portent — un bouton posé sans condition mènerait neuf fois sur dix à
   * une liste vide, qui se lit comme une panne.
   */
  exercices: number
}

export interface ChapitreDuPlan {
  id: number
  slug: string
  titre: string
  niveau: string
  objectifs: string[]
  /**
   * **Le serveur l'envoie encore ; l'écran ne le rend plus** — [DECISIONS §168].
   *
   * C'est une donnée de gestion : elle dit sur quoi repose un chapitre que
   * personne n'a pu adosser à un document, et le chargeur la vérifie. Elle est
   * écrite pour nous, pas pour le candidat — le §122, qui l'affichait sous le
   * chapeau « Hors du document de référence », est amendé.
   *
   * Le type la déclare pour que rien ne la reprenne par inadvertance en la
   * croyant absente de la charge utile.
   */
  etabli_depuis: string | null
  termine: boolean
  ouvert: boolean
  sections: SectionDuPlan[]
}

export interface ModuleDuPlan {
  slug: string
  titre: string
  niveau: string
  chapitres: ChapitreDuPlan[]
}

export interface EtatDeLExamen {
  /** Tous les modules sont clos : c'est le droit d'ACCÈS. */
  ouvert: boolean
  reussi: boolean
  /** Une DATE, pas un booléen — « revenez dans trois heures » ne se déduit pas d'un `false`. */
  reprise_le: string | null
}

export interface OuReprendre {
  chapitre: string
  section: string
  titre: string
}

export interface PlanDuCours {
  id: number
  slug: string
  titre: string
  description_md: string | null
  seuil: number
  modules: ModuleDuPlan[]
  examen: EtatDeLExamen
  /** La première section ouverte et non terminée, ou `null` si tout est fait. */
  reprendre: OuReprendre | null
  /**
   * A-t-il OUVERT le cours ? — et non « en a-t-il terminé une section ».
   *
   * Lire cinq parties de leçon ne termine aucune section : il faut réussir son
   * quiz à 7/10. Confondre les deux faisait proposer « Commencer le cours » à
   * quelqu'un qui le lisait depuis une heure.
   */
  commence: boolean
}

/** Une ligne du catalogue : ce qu'il faut pour choisir, rien de plus. */
export interface CoursDuCatalogue {
  slug: string
  titre: string
  description_md: string | null
  chapitres: number
  lecons: number
  minutes: number
  lecons_faites: number
  /** Débuté — au sens « ouvert », pas « avancé ». */
  commence: boolean
}

export function catalogue(
  recherche: string,
  /**
   * Lire les cours de TOUTES les facultés — [DECISIONS §370], comme pour les
   * archives. Sans demande, le catalogue est celui de la piste du candidat,
   * même pour un admin ; le serveur revérifie le rôle avant de lever la borne.
   */
  toutesLesFacultes = false,
): Promise<{ data: CoursDuCatalogue[] }> {
  const q = recherche.trim()

  const params = new URLSearchParams()

  if (q !== '') params.set('q', q)
  if (toutesLesFacultes) params.set('portee', 'toutes')

  const requete = params.toString()

  return api<{ data: CoursDuCatalogue[] }>(
    `/parcours/cours${requete === '' ? '' : `?${requete}`}`,
  )
}

/**
 * Où j'en suis de mon programme — RG-3.1ter, [DECISIONS §110].
 *
 * **Trois nombres et une comparaison.** `requis_par_jour` est une exigence,
 * `rythme` une mesure ; c'est leur écart qui informe, et il se dit en JOURS —
 * l'unité dans laquelle un candidat peut agir.
 *
 * Les `null` ne sont pas des zéros et ne doivent jamais être affichés comme
 * tels : `rythme: null` veut dire « rien de terminé, donc rien à mesurer »,
 * `jours: null` « pas de date au calendrier ».
 */
export interface AvanceeDuProgramme {
  date_concours: string | null
  jours: number | null
  lecons: number
  lecons_faites: number
  restantes: number
  requis_par_jour: number | null
  rythme: number | null
  /** Positif : il manquerait N jours. Négatif : N jours d'avance. */
  ecart_jours: number | null
}

export function programme(): Promise<{ data: AvanceeDuProgramme }> {
  return api<{ data: AvanceeDuProgramme }>('/parcours/programme')
}

export interface PartieDeLecon {
  ancre: string
  titre: string
  vue: boolean
}

export interface Lecon {
  id: number
  slug: string
  titre: string
  minutes: number
  contenu_md: string | null
  parties: PartieDeLecon[]
  /** Le quiz ne s'ouvre qu'une fois toute la leçon lue. */
  quiz_ouvert: boolean
  objectifs: string[]
  chapitre: { slug: string; titre: string }
  /**
   * Combien d'exercices SERVABLES porte cette section — [DECISIONS §169].
   *
   * **Zéro veut dire « pas de bouton »**, et pas un bouton grisé : il n'y a
   * rien à débloquer, il n'y a rien derrière. Sur 377 sections publiées, 40
   * en portent — un bouton posé sans condition mènerait neuf fois sur dix à
   * une liste vide, qui se lit comme une panne.
   */
  exercices: number
  precedente: { slug: string; titre: string } | null
  suivante: { slug: string; titre: string } | null
}

export function cours(slug: string): Promise<{ data: PlanDuCours }> {
  return api<{ data: PlanDuCours }>(`/parcours/cours/${slug}`)
}

export function lecon(chapitre: string, section: string): Promise<{ data: Lecon }> {
  return api<{ data: Lecon }>(`/parcours/chapitres/${chapitre}/sections/${section}`)
}

/**
 * Marque une partie de leçon comme lue.
 *
 * Rend la liste COMPLÈTE des parties vues, et non un accusé de réception :
 * un écran qui tiendrait son propre compte divergerait au premier
 * rechargement, et le candidat verrait ses coches disparaître.
 */
/**
 * Le geste qui commence un cours — §233.
 *
 * Idempotent côté serveur : la date du PREMIER geste l'emporte, si bien que
 * rouvrir un cours entamé ne le fait pas remonter en tête de « Mes cours ».
 */
export async function commencerLeCours(slug: string): Promise<void> {
  await api<unknown>(`/parcours/cours/${encodeURIComponent(slug)}/commencer`, { method: 'POST' })
}

export function marquerVue(
  sectionId: number,
  ancre: string,
): Promise<{ data: { vues: string[]; quiz_ouvert: boolean } }> {
  return api<{ data: { vues: string[]; quiz_ouvert: boolean } }>(
    `/parcours/sections/${String(sectionId)}/vue`,
    { method: 'POST', body: JSON.stringify({ ancre }) },
  )
}

export interface Verdict {
  reussi: boolean
  /** Rappelée par le serveur : un écran qui en aurait sa propre copie dériverait. */
  moyenne_requise: number
  /**
   * Ce que le bilan doit ouvrir — §71.
   *
   * **Deux formes, et elles ne disent pas la même chose.** Une section rend la
   * section suivante du même chapitre (`slug`) ; un CHAPITRE rend la première
   * leçon du chapitre suivant (`chapitre` + `section`), qui peut vivre dans un
   * autre module. L'écran ne le devine pas : il devrait alors connaître la
   * règle de déblocage, qui vit côté serveur (CA-3.1).
   */
  suivante?: { slug?: string; chapitre?: string; section?: string; titre: string } | null
}

export function terminerLaSection(sectionId: number, score: number): Promise<{ data: Verdict }> {
  return api<{ data: Verdict }>(`/parcours/sections/${String(sectionId)}/terminer`, {
    method: 'POST',
    body: JSON.stringify({ score }),
  })
}

export function quizDeSection(sectionId: number): Promise<{ data: { cartes: Carte[] } }> {
  return api<{ data: { cartes: Carte[] } }>(`/parcours/sections/${String(sectionId)}/quiz`)
}

export function quizDeChapitre(chapitreId: number): Promise<{ data: { cartes: Carte[] } }> {
  return api<{ data: { cartes: Carte[] } }>(`/parcours/chapitres/${String(chapitreId)}/quiz`)
}

export function terminerLeChapitre(
  chapitreId: number,
  score: number,
): Promise<{ data: Verdict }> {
  return api<{ data: Verdict }>(`/parcours/chapitres/${String(chapitreId)}/terminer`, {
    method: 'POST',
    body: JSON.stringify({ score }),
  })
}

export function examenFinal(coursId: number): Promise<{ data: { cartes: Carte[] } }> {
  return api<{ data: { cartes: Carte[] } }>(`/parcours/cours/${String(coursId)}/examen`)
}

export function terminerLExamenFinal(
  coursId: number,
  score: number,
): Promise<{ data: Verdict & { deja_obtenu: boolean } }> {
  return api<{ data: Verdict & { deja_obtenu: boolean } }>(
    `/parcours/cours/${String(coursId)}/examen`,
    { method: 'POST', body: JSON.stringify({ score }) },
  )
}

/** Les deux portes d'un tag — RG-2.38, [DECISIONS §37]. */
export interface PortesDunTag {
  /** Le thème du quiz : premier ancêtre à deux enfants (RG-2.25, RG-2.26). */
  quiz: { path: string; label: string } | null
  /**
   * La leçon que ce thème désigne, ou `null` — [DECISIONS §179].
   *
   * **Jamais un cours entier.** Un thème de branche — `botanique`,
   * `appareil_locomoteur` — recouvre dix chapitres et n'en désigne aucun : le
   * serveur rendait alors le cours de la matière, l'écran affichait la porte,
   * et la lecture répondait 404. Ce qui arrive ici désigne une leçon ouvrable,
   * ou rien. D'où les trois champs non nuls.
   */
  cours: {
    cours_slug: string
    cours_titre: string
    chapitre_id: number
    chapitre_titre: string
    section_id: number
  } | null
}

export function portesDunTag(path: string): Promise<{ data: PortesDunTag }> {
  return api<{ data: PortesDunTag }>(
    `/parcours/tags/portes?path=${encodeURIComponent(path)}`,
  )
}

/**
 * Une leçon lue SANS que rien ne s'enregistre — RG-3.16, [DECISIONS §174].
 *
 * **Deux portes, une seule charge utile.** Un tag du Fil ou des Archives, un
 * exercice de la banque : la lecture est la même, et les deux routes n'écrivent
 * rien. Un second type aurait laissé croire à deux contenus différents, puis à
 * deux composants, puis à deux fois la phrase qui garantit l'absence de coche.
 *
 * Ce que ces routes N'ONT PAS, et délibérément : ni les parties de la leçon, ni
 * l'état des verrous, ni le quiz. Elles ne servent qu'à lire.
 */
export interface LeconEnLectureSeule {
  titre: string
  content_md: string
  cours_titre: string
  chapitre_titre: string
}

/**
 * @param item l'identifiant public de l'item d'où l'on vient — §244. Le serveur
 *   s'en sert pour choisir LA section qui traite la question plutôt que la
 *   première du chapitre. Le texte n'est jamais envoyé : le serveur le relit en
 *   base, sans quoi on pourrait faire pointer la porte où l'on veut.
 */
export function leconDunTag(
  path: string,
  item?: string,
): Promise<{ data: LeconEnLectureSeule }> {
  const item_ = item === undefined ? '' : `&item=${encodeURIComponent(item)}`

  return api<{ data: LeconEnLectureSeule }>(
    `/parcours/tags/lecon?path=${encodeURIComponent(path)}${item_}`,
  )
}

/**
 * La leçon d'une section DÉSIGNÉE — le pont de la banque vers le cours (§174).
 *
 * **Par identifiant, parce que c'est ce que l'exercice connaît.** Lui faire
 * porter le slug du chapitre et celui de la section serait une seconde copie de
 * la hiérarchie, qui divergerait au premier changement de plan.
 *
 * **Ce n'est PAS {@link lecon}.** Celle-là pose la coche de lecture et refuse
 * une section verrouillée ; celle-ci n'écrit rien. La banque est en accès libre
 * (§144) : la faire passer par l'autre route lui aurait donné le pouvoir
 * d'ouvrir le quiz de section, puis le chapitre, puis l'examen final.
 */
export function leconDuneSection(sectionId: number): Promise<{ data: LeconEnLectureSeule }> {
  return api<{ data: LeconEnLectureSeule }>(
    `/parcours/sections/${String(sectionId)}/lecon`,
  )
}
